import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { ErrorBoundary } from "@sentry/react";
import React, { useMemo } from "react";
import { RootStateOrAny, useSelector } from "react-redux";
import { Tooltip } from "react-tippy";
import { ErrorMsg } from "../../../../components/AppMessages";
import LoadingEllipsis from "../../../../components/Loading/LoadingEllipsis";
import { DefaultTable } from "../../../../components/Tables/subs";

const MarketBreakdownTable = () => {
  const { date_range: dateRangeParam } = useSelector((state: RootStateOrAny) => state.parameters);
  const { Current: currentMarketBreakdown, YoY: yoyMarketBreakdown } = useSelector(
    (state: RootStateOrAny) => state.voc.rating_per_touchpoint.market_breakdown
  );
  const { rating_per_touchpoint_market_breakdown: loading } = useSelector((state: RootStateOrAny) => state.loading);

  const columns = useMemo(
    () => [
      {
        id: "touchpoints",
        Header: "Touchpoints",
        accessor: "touchpoint",
        enableRowSpan: true,
        sort: false,
        disableSortBy: true,
      },
      {
        id: "metric",
        Header: "Metric",
        accessor: "metric",
        sort: false,
        disableSortBy: true,
      },
      {
        id: "value",
        Header: "Selected period",
        accessor: "value",
        sort: false,
        disableSortBy: true,
        Cell: (cell: { value: number; row: any }) => {
          const { value } = cell;
          const metric = cell.row.original.metric;
          return (
            <div className={metric == "Rate" ? `${value >= 4 ? "textGreen" : value >= 3.9 ? "textAmber" : "textRed"}` : ""}>
              {value ? value.toLocaleString() : value != 0 ? value : ""}
            </div>
          );
        },
      },
      {
        id: "significant",
        Header: () => {
          return (
            <div style={{ display: "flex" }}>
              <ArrowDownwardIcon htmlColor="#A9A9A9" className="rtp-significance-arrow" />
              <Tooltip
                position="right"
                className={"normal_tippy"}
                trigger="mouseenter"
                animation="none"
                interactive={true}
                delay={1}
                hideDelay={1}
                duration={1}
                title={
                  "Watch out for low statistical significance. A 'down arrow' signifies a low volume of respondents (lower than 16 per month)"
                }
                size={"small"}
              >
                <span className="info_tooltip" />
              </Tooltip>
            </div>
          );
        },
        accessor: "significant",
        sort: false,
        disableSortBy: true,
        Cell: (cell: any) => {
          const showIcon = cell.row.values.significant;
          return !showIcon ? <ArrowDownwardIcon htmlColor="#A9A9A9" className="rtp-significance-arrow" /> : null;
        },
      },
      {
        id: "ytd",
        Header: "Previous period",
        accessor: "yoy_value",
        sort: false,
        disableSortBy: true,
        Cell: (cell: { value: number; row: any }) => {
          const { value } = cell;
          return <div> {value ? value.toLocaleString() : value != 0 ? value : ""}</div>;
        },
      },
      {
        id: "yoy_significant",
        Header: () => {
          return (
            <div style={{ display: "flex" }}>
              <ArrowDownwardIcon htmlColor="#A9A9A9" className="rtp-significance-arrow" />
              <Tooltip
                position="right"
                className={"normal_tippy"}
                trigger="mouseenter"
                animation="none"
                interactive={true}
                delay={1}
                hideDelay={1}
                duration={1}
                title={
                  "Watch out for low statistical significance. A 'down arrow' signifies a low volume of respondents  (lower than 16 per month)"
                }
                size={"small"}
              >
                <span className="info_tooltip" />
              </Tooltip>
            </div>
          );
        },
        accessor: "yoy_significant",
        sort: false,
        disableSortBy: true,
        Cell: (cell: any) => {
          const showIcon = cell.row.values.yoy_significant;
          return !showIcon ? <ArrowDownwardIcon htmlColor="#A9A9A9" className="rtp-significance-arrow" /> : null;
        },
      },
    ],
    [currentMarketBreakdown]
  );

  const tableData = useMemo(() => {
    const data: any = [];
    if (currentMarketBreakdown && yoyMarketBreakdown) {
      currentMarketBreakdown.forEach((item: any) => {
        const yoyEquivalent = yoyMarketBreakdown.find((yoyItem: any) => yoyItem.touchpoint == item.touchpoint);
        data.push(
          {
            touchpoint: item.touchpoint,
            metric: "Rate",
            value: item.rate,
            significant: true,
            yoy_value: yoyEquivalent?.pts_rate,
            yoy_significant: true,
          },
          {
            touchpoint: item.touchpoint,
            metric: "# answers",
            value: item.answers,
            significant: item.significant,
            yoy_value: yoyEquivalent?.pts_answers,
            yoy_significant: yoyEquivalent?.significant,
          }
        );
      });
    }
    return data;
  }, [currentMarketBreakdown, yoyMarketBreakdown]);

  return (
    <ErrorBoundary fallback={<ErrorMsg />}>
      {tableData && tableData.length > 0 && (
        <div className="tile grid-col-span-6 min-h-15">
          <div className="tileHeader">Official Rating Objective</div>
          {loading ? (
            <LoadingEllipsis isLoading={loading} />
          ) : (
            <>
              <DefaultTable
                columns={columns}
                data={tableData}
                id={"marketBreakdownTablePt1"}
                isChildComponent={false}
                initialSortColId={{ id: "touchpoints", desc: false }}
                hiddenCols={[]}
                manualSortBy={true}
                extraClass="market-breakdown-table"
              />
            </>
          )}
        </div>
      )}
    </ErrorBoundary>
  );
};

export default MarketBreakdownTable;
